import React, { Fragment, useState, useEffect, useReducer, useContext } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { Link } from 'react-router-dom';
import { values } from 'lodash';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import Loader from 'LegacyWeployDesignLanguage/loaders/Loader';
import LeftArrowIconButton from 'LegacyWeployDesignLanguage/icons/LeftArrowIconButton';

import NotificationDialog from '../../../components/weployer/common/NotificationDialog';
import ConfirmModal from '../../../components/weployer/common/ConfirmModal';
import { GET_JOB_DETAILS, GET_JOB_POSITIONS_AND_WEPLOYEES } from './graphql/queries';
import {
  JOB_DETAILS_FETCH_SUCCESS,
  JOB_POSITIONS_AND_WEPLOYEES_FETCH_SUCCESS,
  initialState,
  reducer,
} from './reducer';

import {
  currentPositions as currentPositionsMapper,
  pastPositions as pastPositionsMapper,
} from './graphql/mappers';

import Positions from '../../../components/weployer/jobDetails/Positions';
import JobSummaryAndNavigation from '../../../components/weployer/jobDetails/JobSummaryAndNavigation';

import {
  APPROVE_TIMESHEET_PATH,
  CANCEL_POSITION_PATH,
  JOBS_PATH,
  EXTEND_JOB_PATH,
  TRANSFER_JOB_PATH,
  VIEW_PAST_TIMESHEETS_PATH,
  getExternalPath,
  getInternalPath,
  WEPLOYEE_PROFILE_PATH,
} from '../pathHelpers';

import { ACTIVE, CLOSED } from '../common/graphql/constants';

import { PolicyContext, TRANSFER_ORGANISATION_JOBS, VIEW_ORGANISATION_TIMESHEETS } from '../PolicyProvider';

const POLL_INTERVAL = 30000;

const StyledTitle = styled.div`
  ${props => props.theme.typography.subtitle1};
  color: ${props => props.theme.colors.text.primary};
  margin: ${props => props.theme.spacing[2]} 0 ${props => props.theme.spacing[1]};
  padding-left: ${props => props.theme.spacing[2]};
  display: flex;
  align-items: center;
  height: 48px;
`;

const BackLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  grid-area: 1 / 1 / 2 / 2;
  margin-right: ${props => props.theme.spacing[1]};
`;

const PositionsContainer = styled.div`
  margin: 0 ${props => props.theme.spacing[3]};
`;

const defaultModalProps = {
  confirmModalVisible: false,
  cancelModalVisible: false,
  jobTransferFailedModalVisible: false,
  positionId: '',
};

const JobDetails = ({
  match: { params: { jobId } },
  navigateExternal,
  location,
}) => {
  const [store, dispatch] = useReducer(reducer, initialState);
  const [modal, setModal] = useState(defaultModalProps);
  const { actions } = useContext(PolicyContext);

  const { loading: positionsLoading, data: positionsAndWeployees, error: positionsFetchError } = useQuery(GET_JOB_POSITIONS_AND_WEPLOYEES, {
    variables: { id: jobId },
    pollInterval: POLL_INTERVAL,
  });

  const { loading: jobDetailsLoading, data: jobDetails, error: jobDetailsError } = useQuery(GET_JOB_DETAILS, {
    variables: { id: jobId },
  });

  useEffect(() => {
    if (!positionsLoading && positionsAndWeployees) {
      dispatch({ type: JOB_POSITIONS_AND_WEPLOYEES_FETCH_SUCCESS, payload: positionsAndWeployees });
    }
  }, [positionsLoading, positionsAndWeployees, positionsFetchError]);

  const {
    positions,
    weployees,
    timesheets,
    jobs,
  } = store;
  const job = jobs[jobId];
  const {
    confirmModalVisible,
    cancelModalVisible,
    jobTransferFailedModalVisible,
    positionId: positionIdToCancel,
  } = modal;

  const currentPositions = currentPositionsMapper(values(positions));
  const pastPositions = pastPositionsMapper(values(positions));

  const handleExtendJobClick = () => {
    const path = getExternalPath({ pathName: EXTEND_JOB_PATH, pathParams: { jobId }, params: { origin_path: '/weployers/portals/jobs' } });
    navigateExternal(path);
  };

  const handleExtendPositionClick = (positionId) => {
    const path = getExternalPath({ pathName: EXTEND_JOB_PATH, pathParams: { jobId }, params: { origin_path: '/weployers/portals/jobs', 'position_ids[]': positionId } });
    navigateExternal(path);
  };

  const onTimesheetClick = (timesheetId) => {
    const path = getExternalPath({ pathName: APPROVE_TIMESHEET_PATH, pathParams: { timesheetId } });
    navigateExternal(path);
  };

  const handlePositionCancel = (positionId) => {
    const path = getExternalPath({ pathName: CANCEL_POSITION_PATH, pathParams: { positionId } });
    navigateExternal(path);
  };

  const handleCancelFlow = (position) => {
    const startDate = moment(position.startTime);

    // If start date is in the past
    if (startDate.isBefore()) {
      setModal({
        ...defaultModalProps,
        confirmModalVisible: true,
        positionId: position.id,
      });
      return;
    }
    handlePositionCancel(position.id);
  };

  const handleCancelClick = (positionId) => {
    const position = positions[positionId];

    if (position.isCancellable) {
      handleCancelFlow(position);
    } else {
      setModal({
        ...defaultModalProps,
        cancelModalVisible: true,
        positionId,
      });
    }
  };

  const handleViewPastTimesheets = (positionId) => {
    const path = getExternalPath({ pathName: VIEW_PAST_TIMESHEETS_PATH, pathParams: { positionId } });
    navigateExternal(path);
  };

  const handleModalClose = () => setModal({
    ...defaultModalProps,
  });

  const handleTransferJobClick = () => {
    if (job.isTransferrable) {
      const path = getExternalPath({ pathName: TRANSFER_JOB_PATH, pathParams: { jobId } });
      navigateExternal(path);
    } else {
      setModal({
        ...defaultModalProps,
        jobTransferFailedModalVisible: true,
      });
    }
  };

  const handleViewProfileClick = (weployeeId) => {
    const currentPath = encodeURI(location.pathname);
    const path = getExternalPath({ pathName: WEPLOYEE_PROFILE_PATH, pathParams: { weployeeId }, params: { referrer: currentPath } });

    navigateExternal(path);
  };

  useEffect(() => {
    if (!jobDetailsLoading && jobDetails) {
      dispatch({ type: JOB_DETAILS_FETCH_SUCCESS, payload: jobDetails });
    }
  }, [jobDetailsLoading, jobDetails, jobDetailsError]);

  const { belongsToCurrentUser } = job || {};

  const isTransferJobMenuItemVisible = belongsToCurrentUser || actions.includes(TRANSFER_ORGANISATION_JOBS);
  const isExtendJobMenuItemVisible = belongsToCurrentUser && job.status === ACTIVE;

  const isTimesheetViewable = () => (
    [ACTIVE, CLOSED].includes(job.status) && (actions.includes(VIEW_ORGANISATION_TIMESHEETS) || belongsToCurrentUser)
  );

  const renderPositions = (positionList, { showCancelMenuItem = false } = {}) => (
    <Positions
      belongsToCurrentUser={belongsToCurrentUser}
      positions={positionList}
      weployees={weployees}
      onViewProfileClick={handleViewProfileClick}
      onExtendClick={handleExtendPositionClick}
      onTimesheetClick={onTimesheetClick}
      onCancelClick={handleCancelClick}
      onViewPastTimesheets={handleViewPastTimesheets}
      showPastTimesheetMenuItem={isTimesheetViewable()}
      showCancelMenuItem={showCancelMenuItem}
    />
  );

  const confirmModalMessage = (positionId) => {
    const message = 'This Weployee has already started. If your Weployee has unsubmitted timesheets then please ensure all outstanding timesheets are submitted and approved before continuing.';
    if (!positionId) {
      return message;
    }

    const position = positions[positionId];
    let messagePostfix = '';

    if (position.latestApprovedTimesheetId) {
      const timesheetId = position.latestApprovedTimesheetId;
      const timesheetStartDate = timesheets[timesheetId].firstEntryDate;
      const timesheetFinishDate = timesheets[timesheetId].lastEntryDate;
      const lastApprovedTimesheetPeriod = timesheetFinishDate === timesheetStartDate
        ? `${moment(timesheetFinishDate).format('MMM Do')}`
        : `${moment(timesheetStartDate).format('MMM Do')} - ${moment(timesheetFinishDate).format('MMM Do')}`;

      messagePostfix = ` The last timesheet approved was ${lastApprovedTimesheetPeriod}.`;
    }

    return `${message}${messagePostfix}`;
  };

  const previousPath = location.state ? location.state.prevPath : null;

  return (
    <Loader loading={jobDetailsLoading} text="Loading..." >
      <Fragment>
        { job && job.numberOfPositions &&
          <Fragment>
            <JobSummaryAndNavigation
              backLinkRenderer={() => (
                <BackLink to={previousPath || getInternalPath({ pathName: JOBS_PATH })} >
                  <LeftArrowIconButton />
                </BackLink>
              )}
              job={job}
              onTransferJobClick={handleTransferJobClick}
              onTimesheetClick={onTimesheetClick}
              onExtendJobClick={handleExtendJobClick}
              isTransferJobMenuItemVisible={isTransferJobMenuItemVisible}
              isExtendJobMenuItemVisible={isExtendJobMenuItemVisible}
            />
            <NotificationDialog title="Job Transfer Failed" onModalClose={handleModalClose} open={jobTransferFailedModalVisible}>
              <p>Jobs payable by credit card cannot be transferred. Please contact your account director.</p>
            </NotificationDialog>
            <Loader loading={positionsLoading} text="Loading..." >
              <PositionsContainer>
                { !!currentPositions.length &&
                  <Fragment>
                    <StyledTitle>Weployees</StyledTitle>
                    {renderPositions(currentPositions, { showCancelMenuItem: true })}
                    <ConfirmModal
                      open={confirmModalVisible}
                      title="Cancel?"
                      onClose={handleModalClose}
                      onConfirm={() => handlePositionCancel(positionIdToCancel)}
                    >
                      <p>{confirmModalMessage(positionIdToCancel)}</p>
                    </ConfirmModal>
                    <NotificationDialog
                      title="Cancel Position Failed"
                      onModalClose={handleModalClose}
                      open={cancelModalVisible}
                    >
                      <p>This Weployee still has a timesheet to approve. Approve the timesheet and try again.</p>
                    </NotificationDialog>
                  </Fragment>
                }
                { !!pastPositions.length &&
                  <Fragment>
                    <StyledTitle>Past Weployees</StyledTitle>
                    {renderPositions(pastPositions)}
                  </Fragment>
                }
              </PositionsContainer>
            </Loader>
          </Fragment>
        }
      </Fragment>
    </Loader>
  );
};

JobDetails.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      jobId: PropTypes.string.isRequired,
    }),
  }),
  navigateExternal: PropTypes.func.isRequired,
  location: PropTypes.object,
};

JobDetails.defaultProps = {
  match: {
    params: {},
  },
  location: {},
};

export default JobDetails;
